import { Injectable, inject } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CalTokenService {
  private readonly calService = inject(CalAngularService);
  private readonly inFlightRequests = new Map<string, Promise<string | null>>();

  async getAccessToken(scopes: string[]): Promise<string | null> {
    const normalizedScopes = [...new Set((scopes || []).map((scope) => scope?.trim()).filter(Boolean))];
    if (!normalizedScopes.length) {
      return null;
    }

    const requestKey = normalizedScopes.slice().sort().join(' ');
    const existingRequest = this.inFlightRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const tokenRequest = firstValueFrom(this.calService.getAADToken(normalizedScopes, false))
      .then((response: string | { accessToken?: string; secret?: string } | null) => this.extractAccessToken(response))
      .catch((error: unknown) => {
        console.error('Error getting access token from CAL:', error);
        return null;
      })
      .finally(() => {
        this.inFlightRequests.delete(requestKey);
      });

    this.inFlightRequests.set(requestKey, tokenRequest);
    return tokenRequest;
  }

  private extractAccessToken(response: string | { accessToken?: string; secret?: string } | null): string | null {
    if (!response) {
      return null;
    }

    if (typeof response === 'string') {
      const trimmedResponse = response.trim();
      if (!trimmedResponse) {
        return null;
      }
      if (!trimmedResponse.startsWith('{')) {
        return trimmedResponse;
      }

      try {
        const parsed = JSON.parse(trimmedResponse) as { accessToken?: string; secret?: string };
        return parsed.accessToken || parsed.secret || null;
      } catch {
        return null;
      }
    }

    return response.accessToken || response.secret || null;
  }
}
